import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { familyQueries } from '@/features/family-record/queries';
import { mapFamilyDocument, type FamilyDocumentViewModel, type FamilyDocumentType } from '@/features/family-record/mappers/familyDocument';

export interface UploadFamilyDocumentVariables {
  readonly file: File;
  readonly type?: FamilyDocumentType;
  readonly dateObserved?: {
    readonly year?: number;
    readonly month?: number;
    readonly day?: number;
  };
  readonly comments?: string;
}

export function useUploadFamilyDocument(
  guid: string,
): UseMutationResult<FamilyDocumentViewModel, Error, UploadFamilyDocumentVariables> {
  const queryClient = useQueryClient();
  const { queryKey } = familyQueries.documents(guid);

  return useMutation<FamilyDocumentViewModel, Error, UploadFamilyDocumentVariables>({
    meta: { successMessage: 'success.toast.title.document_created' },
    mutationFn: async (variables) => {
      const dto = await apiClient.uploadFamilyAttachment({
        guid,
        file: variables.file,
        ...(variables.type !== undefined ? { type: variables.type } : {}),
        ...(variables.dateObserved !== undefined
          ? { dateObserved: variables.dateObserved }
          : {}),
        ...(variables.comments !== undefined ? { comments: variables.comments } : {}),
      });
      return mapFamilyDocument(dto);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });
}

export function useDeleteFamilyDocument(
  guid: string,
): UseMutationResult<undefined, Error, string> {
  const queryClient = useQueryClient();
  const { queryKey } = familyQueries.documents(guid);

  return useMutation<undefined, Error, string>({
    meta: { successMessage: 'success.toast.title.document_deleted' },
    mutationFn: (attachmentGuid) =>
      apiClient.deleteFamilyAttachment({ guid, attachmentGuid }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });
}
